import React, { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import { Alert, Avatar, Box, Button, Grid, Stack, Tab, Tabs, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../contexts/AuthContext";
import { UiContext } from "../contexts/UiContext";
import { api } from "../api/axios";
import PersonOutlineRoundedIcon from "@mui/icons-material/PersonOutlineRounded";
import CreditCardOutlinedIcon from "@mui/icons-material/CreditCardOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import LightModeOutlinedIcon from "@mui/icons-material/LightModeOutlined";
import DarkModeOutlinedIcon from "@mui/icons-material/DarkModeOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import OpenInNewRoundedIcon from "@mui/icons-material/OpenInNewRounded";
import WorkspacePremiumOutlinedIcon from "@mui/icons-material/WorkspacePremiumOutlined";
import { IconTile, PageHeader, SectionCard } from "../components/ui";

function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`settings-tabpanel-${index}`}
      aria-labelledby={`settings-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ pt: 2.5 }}>{children}</Box>}
    </div>
  );
}

export default function Settings() {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { user, setUser } = useContext(AuthContext);
  const ui = useContext(UiContext);
  const [tabValue, setTabValue] = useState(0);
  
  // Profile state
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // Payment settings state
  const [paymentUrl, setPaymentUrl] = useState("");
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState("");

  useEffect(() => {
    if (user) {
      setName(user.name || "");
      setEmail(user.email || "");
    }
  }, [user]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setMessage("");
    setError("");
    setPaymentError("");
  };

  const handleProfileSave = async () => {
    setError("");
    setMessage("");
    try {
      const { data } = await api.patch("/api/auth/profile", { name });
      setUser({ ...user, name: data.user.name });
      setMessage(t("pr_updated_message", "הפרופיל עודכן בהצלחה"));
    } catch (err) {
      setError(err?.response?.data?.message || "שגיאה בשמירה");
    }
  };

  const openPaymentSettings = async () => {
    setPaymentLoading(true);
    setPaymentError("");
    try {
      const { data } = await api.post("/api/payment/manage-subscription");
      if (data?.url) {
        setPaymentUrl(data.url);
        window.open(data.url, "_blank");
      } else {
        setPaymentError(t("p_payment_no_url", "לא התקבל קישור לניהול התשלום"));
      }
    } catch (err) {
      setPaymentError(err?.response?.data?.message || "שגיאה");
    } finally {
      setPaymentLoading(false);
    }
  };

  const initials = (() => {
    const src = (user?.name || user?.email || "U").trim();
    const parts = src.split(/\s+/).filter(Boolean);
    return parts.length >= 2 ? (parts[0][0] + parts[1][0]).toUpperCase() : src[0]?.toUpperCase() || "U";
  })();

  const themeOption = (mode, label, Icon) => {
    const selected = (ui?.isDark ? "dark" : "light") === mode;
    return (
      <Box
        component="button"
        type="button"
        role="radio"
        aria-checked={selected}
        onClick={() => {
          if (!selected) ui?.toggleThemeMode?.();
        }}
        sx={{
          flex: 1,
          p: 1.5,
          borderRadius: 1.5,
          border: "1px solid",
          borderColor: selected ? "primary.main" : "divider",
          boxShadow: (th) => (selected ? `0 0 0 1px ${th.palette.primary.main}` : "none"),
          bgcolor: selected ? (th) => th.aw.accentSoft : "background.paper",
          color: selected ? "primary.main" : "text.primary",
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 1.25,
          font: "inherit",
          fontWeight: 600,
          textAlign: "start",
          "&:hover": { borderColor: "primary.main" },
          "&:focus-visible": { outline: (th) => `3px solid ${th.palette.primary.main}`, outlineOffset: 2 },
        }}
      >
        <Icon fontSize="small" />
        {label}
      </Box>
    );
  };

  return (
    <Box>
      <PageHeader title={t("settings", "הגדרות")} subtitle={t("settings_subtitle", "ניהול הפרופיל והחשבון שלך")} />

      <Tabs
        value={tabValue}
        onChange={handleTabChange}
        variant={isMobile ? "fullWidth" : "standard"}
        aria-label={t("settings", "הגדרות")}
        sx={{ mb: 1, display: isMobile ? "flex" : "inline-flex" }}
      >
        <Tab
          icon={<PersonOutlineRoundedIcon />}
          iconPosition="start"
          label={t("settings_tab_profile", "פרופיל")}
          id="settings-tab-0"
          aria-controls="settings-tabpanel-0"
          sx={{ minHeight: 36 }}
        />
        <Tab
          icon={<CreditCardOutlinedIcon />}
          iconPosition="start"
          label={t("settings_tab_billing", "חיוב ותשלום")}
          id="settings-tab-1"
          aria-controls="settings-tabpanel-1"
          sx={{ minHeight: 36 }}
        />
      </Tabs>

      {/* Profile Tab */}
      <TabPanel value={tabValue} index={0}>
        {message && (
          <Alert severity="success" sx={{ mb: 2.5 }}>
            {message}
          </Alert>
        )}
        {error && (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            {error}
          </Alert>
        )}
        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={7}>
            <SectionCard title={t("settings_profile_title", "מידע אישי")} icon={<PersonOutlineRoundedIcon />}>
              <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 3 }}>
                <Avatar sx={{ width: 56, height: 56, bgcolor: (th) => th.aw.accentSoft, color: "primary.main", fontWeight: 700 }}>{initials}</Avatar>
                <Box sx={{ minWidth: 0 }}>
                  <Typography sx={{ fontWeight: 700 }} noWrap>
                    {user?.name || "—"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }} noWrap>
                    {user?.email}
                  </Typography>
                </Box>
              </Stack>
              <Stack spacing={2.5}>
                <TextField
                  label={t("name", "שם")}
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  fullWidth
                  helperText={t("settings_name_help", "השם שיוצג בחשבון שלך")}
                />
                <TextField
                  label={t("email", "אימייל")}
                  value={email}
                  disabled
                  fullWidth
                  helperText={t("settings_email_help", "לא ניתן לשנות את כתובת האימייל")}
                />
                <Box>
                  <Button variant="contained" onClick={handleProfileSave} disabled={!name} sx={{ px: 4 }}>
                    {t("save", "שמירה")}
                  </Button>
                </Box>
              </Stack>
            </SectionCard>
          </Grid>
          <Grid item xs={12} lg={5}>
            <SectionCard
              title={t("settings_appearance_title", "מראה")}
              description={t("pr_theme_desc", "בחר את ערכת הנושא המועדפת עליך")}
              icon={<PaletteOutlinedIcon />}
              iconTone="violet"
            >
              <Stack direction="row" spacing={1.5} role="radiogroup" aria-label={t("settings_appearance_title", "מראה")}>
                {themeOption("light", t("pr_light_mode", "מצב בהיר"), LightModeOutlinedIcon)}
                {themeOption("dark", t("pr_dark_mode", "מצב כהה"), DarkModeOutlinedIcon)}
              </Stack>
            </SectionCard>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Billing Tab */}
      <TabPanel value={tabValue} index={1}>
        {paymentError && (
          <Alert severity="error" sx={{ mb: 2.5 }}>
            {paymentError}
          </Alert>
        )}
        <Grid container spacing={2.5}>
          <Grid item xs={12} lg={7}>
            <SectionCard
              title={t("settings_billing_title", "ניהול מנוי ותשלום")}
              description={t("settings_billing_desc", "נהל את פרטי התשלום, שיטות החיוב וההיסטוריה שלך")}
              icon={<CreditCardOutlinedIcon />}
            >
              <Stack
                direction={{ xs: "column", sm: "row" }}
                spacing={2}
                alignItems={{ xs: "flex-start", sm: "center" }}
                sx={{ p: 2.5, borderRadius: 1.5, border: 1, borderColor: "divider", bgcolor: (th) => th.aw.softer }}
              >
                <IconTile size={44} tone="primary">
                  <ReceiptLongOutlinedIcon />
                </IconTile>
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontWeight: 700 }}>{t("settings_payment_portal", "פורטל ניהול תשלומים")}</Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    {t("settings_payment_portal_desc", "עדכן אמצעי תשלום, צפה בחשבוניות והיסטוריה")}
                  </Typography>
                </Box>
                <Button variant="contained" onClick={openPaymentSettings} disabled={paymentLoading} startIcon={<OpenInNewRoundedIcon />} sx={{ flex: "none" }}>
                  {paymentLoading ? t("settings_opening", "פותח...") : t("settings_open_portal", "פתח פורטל ניהול")}
                </Button>
              </Stack>
              {paymentUrl && (
                <Alert severity="info" sx={{ mt: 2 }}>
                  {t("p_payment_open_new_window", "הפורטל נפתח בחלון חדש. אם לא נפתח, לחץ על הכפתור שוב.")}
                </Alert>
              )}
            </SectionCard>
          </Grid>
          <Grid item xs={12} lg={5}>
            <SectionCard
              title={t("settings_subscription_info", "פרטי מנוי")}
              description={t("settings_subscription_desc", "לצפייה ושינוי החבילה שלך")}
              icon={<WorkspacePremiumOutlinedIcon />}
              iconTone="violet"
            >
              <Button variant="outlined" component={RouterLink} to="/plans" fullWidth>
                {t("settings_view_plans", "צפה בחבילות")}
              </Button>
            </SectionCard>
          </Grid>
        </Grid>
      </TabPanel>
    </Box>
  );
}
